import React from 'react';
import { useTranslation } from 'react-i18next';
import { AUTOPLAY_INTERVAL } from '../helpers';
import { useSliderLogic } from '../../../hooks/useSliderLogic';
import type { ProjectsGridProps } from '../types';
import type { LandingBrand } from '../../../graphql/types';
import {
  TopWrapper,
  SliderTrack,
  Slide,
  MainContent,
  ContentWrapper,
  Content,
  Title,
  Subtitle,
  ArrowWrapper,
  Arrow,
  Indicators,
  Indicator,
  SliderLink
} from './styled';

type Placement = LandingBrand['placements'][number];

export const TopProjectsBlock: React.FC<ProjectsGridProps> = ({
  landingBrand
}) => {
  const { t } = useTranslation();
  const slides = (landingBrand?.placements ?? []).reduce<
    {
      image: string;
      title: string;
      subtitle: string;
      url: string;
    }[]
  >((acc, p: Placement) => {
    if (p.coverUrl) {
      acc.push({
        image: p.coverUrl!,
        title: p.name || '',
        subtitle: p.coverSlogan || '',
        url: p.path || p.url || '#'
      });
    }
    return acc;
  }, []);

  const {
    current,
    isAutoplaying,
    goToPrev,
    goToNext,
    goToSlide,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    loaded,
    total
  } = useSliderLogic(slides, AUTOPLAY_INTERVAL, true);

  if (total === 0) return null;

  return (
    <TopWrapper
      data-cy="pasha-top-slider"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <SliderTrack translate={-current * 100}>
        {slides.map((item, index) => (
          <Slide
            key={index}
            background={loaded[index] ? item.image : ''}
            data-cy={`pasha-top-slide-${index}`}
          >
            {!loaded[index] && <div className="placeholder" />}
            <div className="overlay" />
          </Slide>
        ))}
      </SliderTrack>

      <ContentWrapper>
        <MainContent>
          <Content>
            <Title data-cy={`pasha-slide-text-${current}`}>
              {slides[current].title}
            </Title>
            <Subtitle>{slides[current].subtitle}</Subtitle>
            <SliderLink
              href={slides[current].url}
              target="_blank"
              rel="noopener noreferrer"
              data-cy="pasha-top-link"
              data-stat="slide-more-btn"
            >
              {t('pasha.details')}
            </SliderLink>
          </Content>

          <ArrowWrapper>
            <Arrow
              onClick={goToPrev}
              data-cy="pasha-top-left-scroll"
              data-stat="slide-arrow"
              aria-label="Previous slide"
            />
            <Arrow
              onClick={goToNext}
              right
              data-cy="pasha-top-right-scroll"
              data-stat="slide-arrow"
              aria-label="Next slide"
            />
          </ArrowWrapper>

          <Indicators>
            {slides.map((_: unknown, idx: number) => (
              <Indicator
                key={idx}
                active={idx === current}
                autoPlay={isAutoplaying}
                onClick={() => goToSlide(idx)}
                data-cy={`pasha-top-slider-indicator-${idx}`}
              >
                {isAutoplaying && idx === current && (
                  <div className="progress" />
                )}
              </Indicator>
            ))}
          </Indicators>
        </MainContent>
      </ContentWrapper>
    </TopWrapper>
  );
};
